import { useState, useEffect } from 'react'
import {
  Button,
  Input,
  Badge,
  Modal,
  Card,
  toast,
  Toaster,
  Spinner,
  Empty,
} from '../components/ui/index.js'
import { platform } from '../lib/api.js'
import type { PlatformUser } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'

export function PlatformUsers() {
  const { t } = useI18n()
  const [users, setUsers] = useState<PlatformUser[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    try {
      const data = await platform.listUsers()
      setUsers(data)
    } catch {
      toast.error(t('admin.platform.users_load_err'))
    } finally {
      setLoading(false)
    }
  }

  function openModal() {
    setEmail('')
    setDisplayName('')
    setModalOpen(true)
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload: Parameters<typeof platform.inviteUser>[0] = { email }
      if (displayName) payload.display_name = displayName
      await platform.inviteUser(payload)
      toast.success(t('admin.platform.users_invited_toast'))
      setModalOpen(false)
      load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.platform.users_invite_err'))
    } finally {
      setSaving(false)
    }
  }

  async function setActive(user: PlatformUser, isActive: boolean) {
    const key = isActive
      ? 'admin.platform.users_reactivate_confirm'
      : 'admin.platform.users_deactivate_confirm'
    if (!confirm(t(key, { email: user.email ?? '' }))) return
    try {
      await platform.setUserActive(user.id, isActive)
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, is_active: isActive } : u)))
      toast.success(
        isActive
          ? t('admin.platform.users_reactivated_toast')
          : t('admin.platform.users_deactivated_toast'),
      )
    } catch {
      toast.error(
        isActive
          ? t('admin.platform.users_reactivate_err')
          : t('admin.platform.users_deactivate_err'),
      )
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Toaster />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.platform.users_title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('admin.platform.users_subtitle')}</p>
        </div>
        <Button onClick={openModal}>{t('admin.platform.users_invite_new')}</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Spinner className="w-8 h-8 text-indigo-600" />
        </div>
      ) : users.length === 0 ? (
        <Empty
          title={t('admin.platform.users_empty')}
          description={t('admin.platform.users_empty_hint')}
          action={<Button onClick={openModal}>{t('admin.platform.users_invite')}</Button>}
        />
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 font-medium text-gray-500">
                  {t('admin.platform.users_email')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">
                  {t('admin.platform.users_name')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">
                  {t('admin.platform.users_status')}
                </th>
                <th className="py-3 px-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4 text-gray-900">{u.email}</td>
                  <td className="py-3 px-4 text-gray-500">{u.display_name ?? '—'}</td>
                  <td className="py-3 px-4">
                    <Badge color={u.is_active ? 'green' : 'red'}>
                      {u.is_active
                        ? t('admin.platform.users_active')
                        : t('admin.platform.users_inactive')}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-right">
                    {u.is_active ? (
                      <Button size="sm" variant="ghost" onClick={() => setActive(u, false)}>
                        {t('admin.platform.users_deactivate')}
                      </Button>
                    ) : (
                      <Button size="sm" variant="secondary" onClick={() => setActive(u, true)}>
                        {t('admin.platform.users_reactivate')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('admin.platform.users_invite_title')}
        footer={
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              {t('admin.common.cancel')}
            </Button>
            <Button onClick={handleInvite} disabled={saving}>
              {saving ? t('admin.platform.users_inviting') : t('admin.platform.users_invite')}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleInvite} className="space-y-4">
          <p className="text-sm text-gray-500">{t('admin.platform.users_invite_hint')}</p>
          <Input
            label={t('admin.platform.users_email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('admin.platform.users_email_ph')}
            required
          />
          <Input
            label={t('admin.platform.users_name_optional')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('admin.platform.users_name_ph')}
          />
        </form>
      </Modal>
    </div>
  )
}
